import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueries, useQuery } from '@tanstack/react-query';
import Layout from '../components/Layout';
import GlobalVariablesBar from '../components/comparison/GlobalVariablesBar';
import ComparisonTable from '../components/comparison/ComparisonTable';
import AddScenarioColumnModal from '../components/comparison/AddScenarioColumnModal';
import SaveComparisonModal from '../components/comparison/SaveComparisonModal';
import ChatBar from '../components/scenarios/ChatBar';
import { calculateScenario, getDefaultAssumptions } from '../utils/scenarioCalculations';
import { useComparison } from '../context/ComparisonContext';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { exportComparisonPdf } from '../utils/exportComparisonPdf';
import { ArrowLeft, RotateCcw, Save, FileDown, Scale } from 'lucide-react';
import './ComparisonPage.css';

const MAX_COLUMNS = 8;

const DEFAULT_GLOBALS = {
    interestRate: 6.89,
    downPaymentPercent: 20,
    vacancyRate: 5,
    appreciationRate: 3,
    isDIY: false,
};

let nextColId = 1;
function generateColumnId() {
    return `col_${nextColId++}`;
}

const ComparisonPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { session } = useAuth();
    const { compareList, removeFromCompare, setCompareListFromSaved } = useComparison();

    const [globalOverrides, setGlobalOverrides] = useState(DEFAULT_GLOBALS);
    const [columns, setColumns] = useState([]);
    const [perColumnAssumptions, setPerColumnAssumptions] = useState({});
    const [showAddModal, setShowAddModal] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const initializedCols = useRef(new Set());
    const loadingComparisonRef = useRef(false);

    // Fetch full property data for each unique property in compareList
    const propertyQueries = useQueries({
        queries: compareList.map(item => ({
            queryKey: ['property', String(item.property_id)],
            queryFn: async () => {
                const res = await fetch(`${API_URL}/properties/${item.property_id}`);
                if (!res.ok) throw new Error('Failed to load property');
                return res.json();
            },
            staleTime: 5 * 60 * 1000,
        })),
    });

    const loadedProperties = propertyQueries
        .filter(q => q.isSuccess && q.data)
        .map(q => q.data);

    const isLoading = propertyQueries.some(q => q.isLoading);

    // Deduplicated property lookup map
    const propertiesMap = useMemo(() => {
        const map = {};
        loadedProperties.forEach(p => {
            map[String(p.property_id)] = p;
        });
        return map;
    }, [loadedProperties]);

    // Load saved comparison from URL param
    useEffect(() => {
        const comparisonId = searchParams.get('comparison_id');
        if (!comparisonId || !session) return;

        loadingComparisonRef.current = true;

        (async () => {
            try {
                const res = await fetch(`${API_URL}/comparisons/${comparisonId}`, {
                    headers: { 'Authorization': `Bearer ${session.access_token}` },
                });
                if (!res.ok) return;
                const saved = await res.json();

                // Fetch property data for each property_id
                const propertyFetches = saved.property_ids.map(pid =>
                    fetch(`${API_URL}/properties/${pid}`).then(r => r.ok ? r.json() : null)
                );
                const properties = (await Promise.all(propertyFetches)).filter(Boolean);

                // Set compare list from fetched properties
                setCompareListFromSaved(properties.map(p => ({
                    property_id: p.property_id,
                    address: p.address, city: p.city, state: p.state,
                    list_price: p.list_price, beds: p.beds, baths: p.baths,
                    sqft: p.sqft, primary_photo_url: p.primary_photo_url,
                    arv: p.arv, estimated_rent: p.estimated_rent,
                    roi_percentage: p.roi_percentage
                })));

                // Restore global overrides
                setGlobalOverrides(saved.global_overrides || DEFAULT_GLOBALS);

                // Restore columns and assumptions
                const restoredColumns = [];
                const restoredAssumptions = {};
                let maxColNum = 0;
                for (const col of saved.columns) {
                    const num = parseInt(col.columnId?.replace('col_', '') || '0');
                    if (num > maxColNum) maxColNum = num;
                    restoredColumns.push({
                        columnId: col.columnId,
                        propertyId: col.propertyId,
                        scenarioId: col.scenarioId,
                        scenarioName: col.scenarioName,
                    });
                    initializedCols.current.add(col.columnId);
                    restoredAssumptions[col.columnId] = col.assumptions;
                }
                nextColId = maxColNum + 1;
                setColumns(restoredColumns);
                setPerColumnAssumptions(restoredAssumptions);
                // Saved comparison values become the baseline — loaded state shouldn't show as modified.
                setBaselinePerColumnAssumptions(restoredAssumptions);
            } catch (err) {
                console.error('Failed to load comparison:', err);
            } finally {
                loadingComparisonRef.current = false;
            }
        })();
    }, [searchParams, session, setCompareListFromSaved]);

    // Seed one default column per property from compareList
    useEffect(() => {
        if (loadingComparisonRef.current) return;
        setColumns(prev => {
            const existingPropertyIds = new Set(prev.map(c => c.propertyId));
            const newCols = compareList
                .filter(item => !existingPropertyIds.has(String(item.property_id)))
                .map(item => ({
                    columnId: generateColumnId(),
                    propertyId: String(item.property_id),
                    scenarioId: null,
                    scenarioName: null,
                }));
            return newCols.length > 0 ? [...prev, ...newCols] : prev;
        });
    }, [compareList]);

    // Clean up columns when properties are removed from compareList
    useEffect(() => {
        const currentPropertyIds = new Set(compareList.map(p => String(p.property_id)));
        setColumns(prev => {
            const filtered = prev.filter(c => currentPropertyIds.has(c.propertyId));
            if (filtered.length !== prev.length) {
                // Also clean up assumptions and initialized refs
                const removedIds = prev.filter(c => !currentPropertyIds.has(c.propertyId)).map(c => c.columnId);
                removedIds.forEach(id => initializedCols.current.delete(id));
                setPerColumnAssumptions(prevA => {
                    const next = { ...prevA };
                    removedIds.forEach(id => delete next[id]);
                    return next;
                });
                setBaselinePerColumnAssumptions(prevB => {
                    const next = { ...prevB };
                    removedIds.forEach(id => delete next[id]);
                    return next;
                });
                return filtered;
            }
            return prev;
        });
    }, [compareList]);

    // Initialize assumptions for new columns when property data loads
    useEffect(() => {
        columns.forEach(col => {
            if (!initializedCols.current.has(col.columnId) && propertiesMap[col.propertyId]) {
                initializedCols.current.add(col.columnId);
                const property = propertiesMap[col.propertyId];
                const defaults = getDefaultAssumptions(property);
                setPerColumnAssumptions(prev => {
                    if (prev[col.columnId]) return prev; // already has assumptions (e.g. loaded scenario)
                    return { ...prev, [col.columnId]: defaults };
                });
                setBaselinePerColumnAssumptions(prev => {
                    if (prev[col.columnId]) return prev;
                    return { ...prev, [col.columnId]: defaults };
                });
            }
        });
    }, [columns, propertiesMap]);

    // Fetch all saved scenarios for the user
    const { data: allScenarios } = useQuery({
        queryKey: ['scenarios'],
        queryFn: async () => {
            const res = await fetch(`${API_URL}/scenarios`, {
                headers: { 'Authorization': `Bearer ${session?.access_token}` },
            });
            if (!res.ok) return [];
            return res.json();
        },
        enabled: !!session,
        staleTime: 60 * 1000,
    });

    const scenariosByProperty = useMemo(() => {
        if (!allScenarios) return {};
        const map = {};
        for (const s of allScenarios) {
            const key = String(s.property_id);
            if (!map[key]) map[key] = [];
            map[key].push(s);
        }
        return map;
    }, [allScenarios]);

    // Baseline assumptions per column — used as the reference for modified-field highlighting.
    // Updated when a column is initialized, a scenario is loaded, or Reset All is clicked.
    // User edits via updateColumn do NOT update the baseline, so edits show as amber.
    const [baselinePerColumnAssumptions, setBaselinePerColumnAssumptions] = useState({});

    // Compute results for all columns
    const allResults = useMemo(() => {
        const results = {};
        for (const id of Object.keys(perColumnAssumptions)) {
            const merged = { ...perColumnAssumptions[id], ...globalOverrides };
            results[id] = calculateScenario(merged);
        }
        return results;
    }, [perColumnAssumptions, globalOverrides]);

    const updateGlobal = useCallback((field, value) => {
        setGlobalOverrides(prev => ({ ...prev, [field]: value }));
    }, []);

    const updateColumn = useCallback((columnId, field, value) => {
        setPerColumnAssumptions(prev => ({
            ...prev,
            [columnId]: { ...prev[columnId], [field]: value },
        }));
    }, []);

    const removeColumn = useCallback((columnId) => {
        setColumns(prev => {
            const col = prev.find(c => c.columnId === columnId);
            const remaining = prev.filter(c => c.columnId !== columnId);
            // If this was the last column for that property, remove from context
            if (col && !remaining.some(c => c.propertyId === col.propertyId)) {
                removeFromCompare(Number(col.propertyId));
            }
            return remaining;
        });
        setPerColumnAssumptions(prev => {
            const next = { ...prev };
            delete next[columnId];
            return next;
        });
        setBaselinePerColumnAssumptions(prev => {
            const next = { ...prev };
            delete next[columnId];
            return next;
        });
        initializedCols.current.delete(columnId);
    }, [removeFromCompare]);

    const loadScenarioIntoColumn = useCallback((columnId, scenario) => {
        setColumns(prev => prev.map(col =>
            col.columnId === columnId
                ? { ...col, scenarioId: scenario.id, scenarioName: scenario.name }
                : col
        ));
        setPerColumnAssumptions(prev => ({
            ...prev,
            [columnId]: scenario.assumptions,
        }));
        // Loading a scenario re-baselines the column: the scenario's values are now the reference,
        // so fields don't appear modified until the user edits them on top of the scenario.
        setBaselinePerColumnAssumptions(prev => ({
            ...prev,
            [columnId]: scenario.assumptions,
        }));
    }, []);

    const addColumnFromModal = useCallback((propertyId, scenario) => {
        if (columns.length >= MAX_COLUMNS) return;
        const colId = generateColumnId();
        if (scenario) {
            // Saved scenario
            setColumns(prev => [...prev, {
                columnId: colId,
                propertyId: String(propertyId),
                scenarioId: scenario.id,
                scenarioName: scenario.name,
            }]);
            initializedCols.current.add(colId);
            setPerColumnAssumptions(prev => ({ ...prev, [colId]: scenario.assumptions }));
            setBaselinePerColumnAssumptions(prev => ({ ...prev, [colId]: scenario.assumptions }));
        } else {
            // Default assumptions
            const property = propertiesMap[String(propertyId)];
            if (!property) return;
            const defaults = getDefaultAssumptions(property);
            setColumns(prev => [...prev, {
                columnId: colId,
                propertyId: String(propertyId),
                scenarioId: null,
                scenarioName: null,
            }]);
            initializedCols.current.add(colId);
            setPerColumnAssumptions(prev => ({ ...prev, [colId]: defaults }));
            setBaselinePerColumnAssumptions(prev => ({ ...prev, [colId]: defaults }));
        }
        setShowAddModal(false);
    }, [columns.length, propertiesMap]);

    const resetAll = useCallback(() => {
        setGlobalOverrides(DEFAULT_GLOBALS);
        // Rebuild one default column per property
        const newColumns = [];
        const newAssumptions = {};
        initializedCols.current.clear();
        compareList.forEach(item => {
            const pid = String(item.property_id);
            const property = propertiesMap[pid];
            if (!property) return;
            const colId = generateColumnId();
            newColumns.push({
                columnId: colId,
                propertyId: pid,
                scenarioId: null,
                scenarioName: null,
            });
            initializedCols.current.add(colId);
            newAssumptions[colId] = getDefaultAssumptions(property);
        });
        setColumns(newColumns);
        setPerColumnAssumptions(newAssumptions);
        setBaselinePerColumnAssumptions(newAssumptions);
    }, [compareList, propertiesMap]);

    const handleExportPdf = useCallback(() => {
        exportComparisonPdf(columns, propertiesMap, perColumnAssumptions, allResults, globalOverrides);
    }, [columns, propertiesMap, perColumnAssumptions, allResults, globalOverrides]);

    // Empty state
    if (compareList.length === 0) {
        return (
            <Layout scrollable={true}>
                <div className="compare-page">
                    <div className="compare-empty">
                        <Scale size={48} strokeWidth={1.5} />
                        <h2>No Properties Selected</h2>
                        <p>Add properties to compare from the search page.</p>
                        <button className="compare-empty-btn" onClick={() => navigate('/search')}>
                            Go to Search
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout scrollable={true}>
            <div className="compare-page">
                <div className="compare-header">
                    <button className="compare-back-link" onClick={() => navigate(-1)}>
                        <ArrowLeft size={16} />
                        Back
                    </button>
                    <h1 className="compare-title">Compare Properties</h1>
                    <div className="compare-header-actions">
                        <button className="compare-btn-reset" onClick={resetAll}>
                            <RotateCcw size={14} />
                            Reset All
                        </button>
                        <button className="compare-btn-export" onClick={handleExportPdf}>
                            <FileDown size={14} />
                            Export PDF
                        </button>
                        <button className="compare-btn-save" onClick={() => setShowSaveModal(true)}>
                            <Save size={14} />
                            Save Comparison
                        </button>
                    </div>
                </div>

                <GlobalVariablesBar globals={globalOverrides} onUpdate={updateGlobal} defaults={DEFAULT_GLOBALS} />

                {isLoading ? (
                    <div className="compare-loading">Loading property data...</div>
                ) : (
                    <ComparisonTable
                        columns={columns}
                        propertiesMap={propertiesMap}
                        perColumnAssumptions={perColumnAssumptions}
                        results={allResults}
                        onUpdateColumn={updateColumn}
                        onRemoveColumn={removeColumn}
                        savedScenarios={scenariosByProperty}
                        onLoadScenario={loadScenarioIntoColumn}
                        maxReached={columns.length >= MAX_COLUMNS}
                        onOpenAddModal={() => setShowAddModal(true)}
                        defaultPerColumnAssumptions={baselinePerColumnAssumptions}
                    />
                )}
            </div>

            <ChatBar />

            <AddScenarioColumnModal
                isOpen={showAddModal}
                onClose={() => setShowAddModal(false)}
                properties={compareList}
                savedScenarios={scenariosByProperty}
                onAdd={addColumnFromModal}
            />

            <SaveComparisonModal
                isOpen={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                compareList={compareList}
                globalOverrides={globalOverrides}
                columns={columns}
                perColumnAssumptions={perColumnAssumptions}
                onSaved={() => setShowSaveModal(false)}
            />
        </Layout>
    );
};

export default ComparisonPage;
